import { createSignal, For, onCleanup } from 'solid-js';

// ─── Brand colors ────────────────────────────────────────────
const COLORS = {
  kho: '#e67e22', // warm orange
  a: '#d4a574', // light brown / tan
  i: '#c0392b', // accent red-orange for the dot
  dot: '#e67e22',
};

const LETTERS: { char: string; color: string }[] = [
  { char: 'k', color: COLORS.kho },
  { char: 'h', color: COLORS.kho },
  { char: 'o', color: COLORS.kho },
  { char: 'a', color: COLORS.a },
  { char: 'i', color: COLORS.a },
];

// ─── Effect registry ─────────────────────────────────────────
// Each effect returns a CSS transform/style per letter index,
// plus a duration in ms.

interface LetterStyle {
  transform: string;
  opacity: string;
  transition: string;
  color?: string;
  filter?: string;
  display?: string;
}

interface Effect {
  name: string;
  duration: number;
  /** style at the "active" keyframe, per index (0-4) */
  style: (i: number, total: number, tick: number) => Partial<LetterStyle>;
}

const effects: Effect[] = [
  // 1) Bounce — letters jump up in a wave
  {
    name: 'bounce',
    duration: 600,
    style: (i, _, tick) => {
      const delay = i * 60;
      const t = Math.max(0, tick - delay);
      const y = t < 300 ? Math.sin((t / 300) * Math.PI) * -12 : 0;
      return { transform: `translateY(${y}px)` };
    },
  },
  // 2) Buzz — rapid horizontal shake
  {
    name: 'buzz',
    duration: 400,
    style: (_i, _, tick) => {
      const x = tick < 400 ? Math.sin(tick * 0.15) * 3 : 0;
      return { transform: `translateX(${x}px)` };
    },
  },
  // 3) Scale pop — each letter pops bigger in sequence
  {
    name: 'pop',
    duration: 500,
    style: (i, _, tick) => {
      const delay = i * 70;
      const t = Math.max(0, tick - delay);
      const s = t < 250 ? 1 + Math.sin((t / 250) * Math.PI) * 0.35 : 1;
      return { transform: `scale(${s})` };
    },
  },
  // 4) Rubber band stretch
  {
    name: 'rubber',
    duration: 600,
    style: (_i, _, tick) => {
      const p = tick / 600;
      let sx = 1,
        sy = 1;
      if (p < 0.3) {
        sx = 1 + p * 1.2;
        sy = 1 - p * 0.4;
      } else if (p < 0.5) {
        sx = 1.36 - (p - 0.3) * 3;
        sy = 0.88 + (p - 0.3) * 1.5;
      } else if (p < 0.7) {
        sx = 0.76 + (p - 0.5) * 1.5;
        sy = 1.18 - (p - 0.5) * 1;
      } else {
        sx = 1 + (1 - p) * 0.2;
        sy = 1 - (1 - p) * 0.1;
      }
      return { transform: `scale(${sx}, ${sy})` };
    },
  },
  // 5) Wave — smooth sine wave
  {
    name: 'wave',
    duration: 800,
    style: (i, _, tick) => {
      const y = Math.sin((tick / 800) * Math.PI * 2 - i * 0.8) * 6;
      return { transform: `translateY(${y}px)` };
    },
  },
  // 6) Spin — each letter does a full spin
  {
    name: 'spin',
    duration: 600,
    style: (i, _, tick) => {
      const delay = i * 80;
      const t = Math.max(0, tick - delay);
      const deg = Math.min(t / 400, 1) * 360;
      return { transform: `rotateY(${deg}deg)` };
    },
  },
  // 7) Color flash — letters flash to bright colors then back
  {
    name: 'flash',
    duration: 500,
    style: (i, _, tick) => {
      const delay = i * 60;
      const t = Math.max(0, tick - delay);
      const bright = t > 0 && t < 300;
      const hue = (i * 60 + 20) % 360;
      return {
        filter: bright ? 'brightness(1.6) saturate(1.5)' : 'none',
        color: bright ? `hsl(${hue}, 80%, 55%)` : undefined,
      };
    },
  },
  // 8) Scatter & regroup
  {
    name: 'scatter',
    duration: 700,
    style: (i, total, tick) => {
      const mid = total / 2;
      const dir = i - mid;
      const p = tick / 700;
      let dx = 0,
        dy = 0,
        o = 1;
      if (p < 0.4) {
        const t = p / 0.4;
        dx = dir * 18 * t;
        dy = (Math.random() > 0.5 ? -1 : 1) * 10 * t;
        o = 1 - t * 0.3;
      } else {
        const t = (p - 0.4) / 0.6;
        dx = dir * 18 * (1 - t);
        dy *= 1 - t;
        o = 0.7 + t * 0.3;
      }
      return { transform: `translate(${dx}px, ${dy}px)`, opacity: `${o}` };
    },
  },
  // 9) Tilt seesaw
  {
    name: 'tilt',
    duration: 500,
    style: (i, total, tick) => {
      const center = (total - 1) / 2;
      const offset = i - center;
      const angle = Math.sin((tick / 500) * Math.PI) * offset * 12;
      return { transform: `rotate(${angle}deg)` };
    },
  },
  // 10) Jelly — squish then overshoot
  {
    name: 'jelly',
    duration: 600,
    style: (i, _, tick) => {
      const delay = i * 50;
      const t = Math.max(0, tick - delay) / 400;
      let s = 1;
      if (t < 1) {
        s = 1 + Math.sin(t * Math.PI * 3) * 0.2 * (1 - t);
      }
      return { transform: `scaleX(${s}) scaleY(${2 - s})` };
    },
  },
  // 11) Domino fall & recover
  {
    name: 'domino',
    duration: 700,
    style: (i, _, tick) => {
      const delay = i * 90;
      const t = Math.max(0, tick - delay);
      let angle = 0;
      if (t < 200) angle = (t / 200) * 30;
      else if (t < 500) angle = 30 - ((t - 200) / 300) * 30;
      return {
        transform: `rotate(${angle}deg) translateY(${angle > 0 ? 2 : 0}px)`,
      };
    },
  },
  // 12) Typewriter — letters appear one by one
  {
    name: 'typewriter',
    duration: 600,
    style: (i, _, tick) => {
      const delay = i * 100;
      const visible = tick > delay;
      return {
        opacity: visible ? '1' : '0',
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
      };
    },
  },
  // 13) Drop — random letters drop off-screen then rise back up
  {
    name: 'drop',
    duration: 900,
    style: (i, _total, tick) => {
      // Use a deterministic "random" per-letter based on index
      const drops = [0, 2, 4]; // which indices drop (k, o, i)
      if (!drops.includes(i)) return {};
      const delay = drops.indexOf(i) * 120;
      const t = Math.max(0, tick - delay);
      const p = t / (900 - delay);
      let y = 0,
        o = 1;
      if (p < 0.35) {
        // fall down
        const f = p / 0.35;
        y = f * f * 50; // accelerate
        o = 1 - f * 0.8;
      } else if (p < 0.55) {
        // hidden
        y = 50;
        o = 0;
      } else {
        // rise back
        const f = (p - 0.55) / 0.45;
        y = (1 - f) * -20;
        o = f;
      }
      return { transform: `translateY(${y}px)`, opacity: `${o}` };
    },
  },
];

// ─── Factory effects (need per-run random state) ────────────
// Each factory returns a fresh Effect instance with captured randomness.

const effectFactories: (() => Effect)[] = [
  // 14) Shuffle — letters swap positions then slide back
  () => {
    // Fisher-Yates shuffle to get a random permutation
    const perm = LETTERS.map((_, i) => i);
    for (let i = perm.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [perm[i], perm[j]] = [perm[j]!, perm[i]!];
    }
    // Ensure at least one swap happened
    if (perm.every((v, i) => v === i)) {
      [perm[0], perm[perm.length - 1]] = [perm[perm.length - 1]!, perm[0]!];
    }
    // Average character width in px (approximate for extrabold text)
    const charW = 12;
    return {
      name: 'shuffle',
      duration: 1000,
      style: (i: number, _total: number, tick: number) => {
        const target = perm[i]!;
        const dx = (target - i) * charW;
        const p = tick / 1000;
        let x: number, o: number;
        if (p < 0.15) {
          // fade out slightly & rise
          const t = p / 0.15;
          x = 0;
          o = 1 - t * 0.3;
        } else if (p < 0.55) {
          // slide to new position
          const t = (p - 0.15) / 0.4;
          const ease = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
          x = dx * ease;
          o = 0.7;
        } else if (p < 0.75) {
          // hold at swapped position
          x = dx;
          o = 0.7 + ((p - 0.55) / 0.2) * 0.3;
        } else {
          // slide back home
          const t = (p - 0.75) / 0.25;
          const ease = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
          x = dx * (1 - ease);
          o = 1;
        }
        return { transform: `translateX(${x}px)`, opacity: `${o}` };
      },
    };
  },
];

// ─── Component ───────────────────────────────────────────────

export default function KhoaiLogo(props: { class?: string }) {
  const [letterStyles, setLetterStyles] = createSignal<Partial<LetterStyle>[]>(
    LETTERS.map(() => ({}))
  );
  let animFrame = 0;

  onCleanup(() => {
    cancelAnimationFrame(animFrame);
  });

  function pickRandom(): Effect {
    const totalCount = effects.length + effectFactories.length;
    const idx = Math.floor(Math.random() * totalCount);
    if (idx < effects.length) return effects[idx]!;
    return effectFactories[idx - effects.length]!();
  }

  function runEffect() {
    const effect = pickRandom();
    const start = performance.now();

    function tick() {
      const elapsed = performance.now() - start;
      if (elapsed > effect.duration) {
        setLetterStyles(LETTERS.map(() => ({})));
        return;
      }
      setLetterStyles(
        LETTERS.map((_, i) => effect.style(i, LETTERS.length, elapsed))
      );
      animFrame = requestAnimationFrame(tick);
    }

    cancelAnimationFrame(animFrame);
    animFrame = requestAnimationFrame(tick);
  }

  // On hover: trigger an effect (debounce so rapid re-entry doesn't stack)
  let lastTrigger = 0;
  function handleHover() {
    const now = Date.now();
    if (now - lastTrigger < 300) return;
    lastTrigger = now;
    runEffect();
  }

  function handleClick() {
    runEffect();
  }

  return (
    <span
      class={`inline-flex cursor-pointer select-none items-baseline font-extrabold ${props.class ?? ''}`}
      style={{ perspective: '400px' }}
      onMouseEnter={handleHover}
      onClick={handleClick}
    >
      <For each={LETTERS}>
        {(letter, idx) => (
          <span
            style={{
              color: letterStyles()[idx()]?.color ?? letter.color,
              transform: letterStyles()[idx()]?.transform ?? 'none',
              opacity: letterStyles()[idx()]?.opacity ?? '1',
              filter: letterStyles()[idx()]?.filter ?? 'none',
              transition: 'color 0.2s, filter 0.2s',
              display: 'inline-block',
              'transform-origin': 'center bottom',
              'will-change': 'transform, opacity, filter',
            }}
          >
            {letter.char}
          </span>
        )}
      </For>
      <span style={{ color: COLORS.dot, 'margin-left': '1px' }}>.io</span>
    </span>
  );
}
